<template>
  <div class="flex flex-col min-h-screen w-full mx-auto safe-area-insets">
    <!-- Homepage State -->
    <section
      v-if="currentState === 'homepage'"
      class="flex-1 flex flex-col items-center justify-center text-center gap-6 min-h-screen p-4 md:p-6 lg:p-8"
    >
      <header aria-labelledby="auth-main-title" class="homepage-header">
        <h1
          id="auth-main-title"
          class="game-title font-display text-arena-text-bright mb-2 font-extrabold tracking-[0.08em]"
        >
          {{ STRINGS.branding.title }}
        </h1>
        <p
          class="game-subtitle text-sm text-arena-text-muted mb-6 uppercase tracking-[0.15em] font-medium lg:text-xl"
        >
          {{ STRINGS.branding.subtitle }}
        </p>

        <div class="vs-motif flex items-center justify-center gap-4 mb-6" aria-hidden="true">
          <span
            class="vs-line flex-1 max-w-[80px] h-px bg-gradient-to-r from-transparent via-energy-primary to-transparent"
          ></span>
          <span
            class="vs-text font-display text-lg font-extrabold text-energy-primary tracking-[0.15em] -rotate-6 inline-block"
            >VS</span
          >
          <span
            class="vs-line flex-1 max-w-[80px] h-px bg-gradient-to-r from-transparent via-energy-primary to-transparent"
          ></span>
        </div>
      </header>

      <div aria-labelledby="auth-content-heading">
        <h2 id="auth-content-heading" class="sr-only">
          {{ STRINGS.aria.descriptions.gameIntroductionHeading }}
        </h2>

        <section class="player-setup-section flex flex-col items-center gap-4">
          <p class="text-base text-arena-text-muted m-0 mb-2">
            {{ STRINGS.auth.flavorText }}
          </p>

          <PrimeButton
            @click="navigateTo('login')"
            :aria-label="STRINGS.aria.labels.logIn"
            data-testid="homepage-login-button"
            class="min-h-12 min-w-12 touch-manipulation"
          >
            {{ STRINGS.auth.buttons.logIn }}
          </PrimeButton>

          <PrimeButton
            variant="secondary"
            @click="navigateTo('signup')"
            :aria-label="STRINGS.aria.labels.createAccount"
            data-testid="homepage-signup-button"
            class="min-h-12 min-w-12 touch-manipulation"
          >
            {{ STRINGS.auth.buttons.createAccount }}
          </PrimeButton>
        </section>
      </div>

      <FooterLegal @navigate="handleAuthNavigate" />
    </section>

    <!-- Login State -->
    <section
      v-else-if="currentState === 'login'"
      class="flex-1 flex flex-col items-center justify-center text-center gap-6 min-h-screen p-4 md:p-6 lg:p-8"
    >
      <AuthLoginForm @navigate="handleAuthNavigate" />
    </section>

    <!-- Signup State -->
    <section
      v-else-if="currentState === 'signup'"
      class="flex-1 flex flex-col items-center justify-center text-center gap-6 min-h-screen p-4 md:p-6 lg:p-8"
    >
      <AuthSignupForm
        :invite-token="props.inviteToken ?? undefined"
        @success="handleSignupSuccess"
        @navigate="handleAuthNavigate"
      />
    </section>

    <!-- Forgot Password State -->
    <section
      v-else-if="currentState === 'forgot-password'"
      class="flex-1 flex flex-col items-center justify-center text-center gap-6 min-h-screen p-4 md:p-6 lg:p-8"
    >
      <AuthForgotPasswordForm @navigate="handleAuthNavigate" />
    </section>

    <!-- Reset Password State -->
    <section
      v-else-if="currentState === 'reset-password'"
      class="flex-1 flex flex-col items-center justify-center text-center gap-6 min-h-screen p-4 md:p-6 lg:p-8"
    >
      <AuthResetPasswordForm v-if="resetToken" :token="resetToken" @navigate="handleAuthNavigate" />
      <AuthForgotPasswordForm v-else @navigate="handleAuthNavigate" />
    </section>

    <!-- About State -->
    <section
      v-else-if="currentState === 'about'"
      class="flex-1 flex flex-col min-h-screen p-4 md:p-6 lg:p-8"
    >
      <ContentPage page="about" @navigate="handleAuthNavigate" />
    </section>

    <!-- Privacy State -->
    <section
      v-else-if="currentState === 'privacy'"
      class="flex-1 flex flex-col min-h-screen p-4 md:p-6 lg:p-8"
    >
      <ContentPage page="privacy" @navigate="handleAuthNavigate" />
    </section>

    <!-- Terms State -->
    <section
      v-else-if="currentState === 'terms'"
      class="flex-1 flex flex-col min-h-screen p-4 md:p-6 lg:p-8"
    >
      <ContentPage page="terms" @navigate="handleAuthNavigate" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import PrimeButton from './ui/PrimeButton.vue';
import AuthLoginForm from './AuthLoginForm.vue';
import AuthSignupForm from './AuthSignupForm.vue';
import AuthForgotPasswordForm from './AuthForgotPasswordForm.vue';
import AuthResetPasswordForm from './AuthResetPasswordForm.vue';
import ContentPage from './ContentPage.vue';
import FooterLegal from './FooterLegal.vue';
import type { MatchmakingScreenState, NavigationState } from '../types/websocket';
import STRINGS from '@/constants/strings';

const props = defineProps<{ inviteToken?: string | null }>();

const currentState = ref<MatchmakingScreenState>('homepage');
const resetToken = ref<string | null>(null);

const VALID_NAV_STATES: readonly MatchmakingScreenState[] = [
  'homepage',
  'login',
  'signup',
  'forgot-password',
  'reset-password',
  'about',
  'privacy',
  'terms',
];

// Push a history entry each time we change auth sub-view so the browser
// Back button returns to the previous sub-view instead of exiting the SPA.
const navigateTo = (target: MatchmakingScreenState) => {
  if (currentState.value === target) return;
  window.history.pushState({ authState: target }, '');
  currentState.value = target;
};

const handleSignupSuccess = (_name: string) => {
  navigateTo('homepage');
};

const handleAuthNavigate = (target: NavigationState) => {
  navigateTo(target);
};

const handlePopState = (event: PopStateEvent) => {
  const restored = event.state?.authState;
  if (typeof restored === 'string' && (VALID_NAV_STATES as readonly string[]).includes(restored)) {
    currentState.value = restored as MatchmakingScreenState;
  }
};

onMounted(() => {
  let initial: MatchmakingScreenState = 'homepage';
  let path = window.location.pathname;

  if (props.inviteToken || path === '/signup') {
    initial = 'signup';
  }

  const params = new URLSearchParams(window.location.search);
  const token = params.get('reset_token');
  if (token) {
    resetToken.value = token;
    initial = 'reset-password';
    path = window.location.pathname;
    window.history.replaceState({ authState: initial }, '', path);
  } else {
    window.history.replaceState({ authState: initial }, '');
  }

  currentState.value = initial;
  window.addEventListener('popstate', handlePopState);
});

onUnmounted(() => {
  window.removeEventListener('popstate', handlePopState);
});
</script>

<style scoped>
.game-title {
  font-size: clamp(48px, 10vw, 72px);
  text-shadow: 0 2px 12px rgba(201, 168, 76, 0.3);
  animation: homepageEntrance 0.8s ease-out both;
}

.game-subtitle {
  animation: homepageEntrance 0.8s ease-out 0.15s both;
}

.vs-motif {
  animation: homepageEntrance 0.8s ease-out 0.3s both;
}

@keyframes homepageEntrance {
  from {
    transform: translateY(16px);
  }
  to {
    transform: translateY(0);
  }
}

.player-setup-section {
  animation: homepageEntrance 0.8s ease-out 0.45s both;
}

@media (min-width: 1024px) {
  .game-title {
    font-size: 3.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .game-title,
  .game-subtitle,
  .vs-motif,
  .player-setup-section {
    animation: none !important;
  }
}
</style>
